<template>
  <div class="admin-dashboard">
    <header class="admin-header">
      <div class="admin-nav">
        <div class="admin-brand">
          <h1>Admin Dashboard</h1>
          <router-link to="/" class="back-to-site">
            <i class="fas fa-globe"></i>
            Back to Website
          </router-link>
        </div>
        <div class="header-actions">
          <router-link to="/admin/posts/new" class="btn btn-primary">
            <i class="fas fa-plus"></i>
            <span class="btn-text">New Post</span>
          </router-link>
          <button @click="handleLogout" class="btn btn-secondary">
            <i class="fas fa-sign-out-alt"></i>
            <span class="btn-text">Logout</span>
          </button>
        </div>
      </div>
    </header>

    <main class="dashboard-content">
      <div class="dashboard-stats">
        <div class="stat-card">
          <h3>Total Posts</h3>
          <p class="stat-number">{{ posts.total || 0 }}</p>
        </div>
        <div class="stat-card">
          <h3>Published</h3>
          <p class="stat-number">{{ publishedCount }}</p>
        </div>
        <div class="stat-card">
          <h3>Drafts</h3>
          <p class="stat-number">{{ draftCount }}</p>
        </div>
      </div>

      <div class="posts-section">
        <div class="section-header">
          <h2>Blog Posts</h2>
          <div class="filters">
            <select v-model="statusFilter" @change="loadPosts">
              <option value="all">All Posts</option>
              <option value="published">Published</option>
              <option value="draft">Drafts</option>
            </select>
          </div>
        </div>

        <div v-if="loading" class="loading">
          Loading posts...
        </div>

        <div v-else-if="error" class="error">
          {{ error }}
          <button @click="loadPosts" class="btn btn-secondary">Retry</button>
        </div>

        <div v-else-if="filteredPosts.length === 0" class="empty-state">
          <i class="fas fa-file-alt"></i>
          <p>No posts found</p>
          <router-link to="/admin/posts/new" class="btn btn-primary">
            Create your first post
          </router-link>
        </div>

        <div v-else class="posts-table">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Created</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="post in filteredPosts" :key="post.id">
                <td>
                  <div class="post-title">
                    <strong>{{ post.title }}</strong>
                    <span class="post-slug">{{ post.slug }}</span>
                  </div>
                </td>
                <td>
                  <span 
                    :class="['status-badge', post.published ? 'published' : 'draft']"
                  >
                    {{ post.published ? 'Published' : 'Draft' }}
                  </span>
                </td>
                <td>{{ formatDate(post.created_at) }}</td>
                <td>{{ post.views || 0 }}</td>
                <td>
                  <div class="action-buttons">
                    <router-link 
                      :to="{ name: 'EditPost', params: { id: post.id } }"
                      class="btn-icon"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </router-link>
                    
                    <button
                      v-if="post.published"
                      @click="unpublishPost(post)"
                      class="btn-icon"
                      title="Unpublish"
                    >
                      <i class="fas fa-eye-slash"></i>
                    </button>
                    
                    <button
                      v-else
                      @click="publishPost(post)"
                      class="btn-icon"
                      title="Publish"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    
                    <button
                      @click="deletePost(post)"
                      class="btn-icon btn-danger"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="posts.total > posts.limit" class="pagination">
          <button 
            @click="previousPage" 
            :disabled="currentPage === 1"
            class="btn btn-secondary"
          >
            Previous
          </button>
          <span class="page-info">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages"
            class="btn btn-secondary"
          >
            Next
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/services/auth';
import { useBlog } from '@/services/blog';

export default {
  name: 'AdminDashboard',
  setup() {
    const router = useRouter();
    const { logout } = useAuth();
    const { getAllPosts, deletePost: deletePostApi, publishPost: publishPostApi, unpublishPost: unpublishPostApi } = useBlog();
    
    const posts = ref({ posts: [], total: 0, limit: 20, offset: 0 });
    const loading = ref(true);
    const error = ref('');
    const statusFilter = ref('all');
    const currentPage = ref(1);

    const filteredPosts = computed(() => {
      if (statusFilter.value === 'all') return posts.value.posts;
      if (statusFilter.value === 'published') return posts.value.posts.filter(p => p.published);
      if (statusFilter.value === 'draft') return posts.value.posts.filter(p => !p.published);
      return posts.value.posts;
    });

    const publishedCount = computed(() => 
      posts.value.posts.filter(p => p.published).length
    );

    const draftCount = computed(() => 
      posts.value.posts.filter(p => !p.published).length
    );

    const totalPages = computed(() => 
      Math.ceil(posts.value.total / posts.value.limit)
    );

    const loadPosts = async () => {
      loading.value = true;
      error.value = '';
      
      try {
        const offset = (currentPage.value - 1) * posts.value.limit;
        const result = await getAllPosts(posts.value.limit, offset);
        posts.value = result;
      } catch (err) {
        error.value = 'Failed to load posts. Please try again.';
        console.error('Error loading posts:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleLogout = async () => {
      await logout();
      router.push({ name: 'AdminLogin' });
    };

    const publishPost = async (post) => {
      try {
        await publishPostApi(post.id);
        post.published = true;
      } catch (err) {
        alert('Failed to publish post: ' + err.message);
      }
    };

    const unpublishPost = async (post) => {
      try {
        await unpublishPostApi(post.id);
        post.published = false;
      } catch (err) {
        alert('Failed to unpublish post: ' + err.message);
      }
    };

    const deletePost = async (post) => {
      if (!confirm(`Are you sure you want to delete "${post.title}"?`)) {
        return;
      }
      
      try {
        await deletePostApi(post.id);
        posts.value.posts = posts.value.posts.filter(p => p.id !== post.id);
        posts.value.total--;
      } catch (err) {
        alert('Failed to delete post: ' + err.message);
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        loadPosts();
      }
    };

    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        loadPosts();
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString();
    };

    onMounted(() => {
      loadPosts();
    });

    return {
      posts,
      loading,
      error,
      statusFilter,
      currentPage,
      filteredPosts,
      publishedCount,
      draftCount,
      totalPages,
      loadPosts,
      handleLogout,
      publishPost,
      unpublishPost,
      deletePost,
      nextPage,
      previousPage,
      formatDate,
    };
  },
};
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background: #f8f9fa;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1001;
  overflow-y: auto;
}

.admin-header {
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: #fff;
  padding: 0;
  border-bottom: 1px solid #e1e5e9;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  height: 4.5rem;
  display: flex;
  align-items: center;
}

.admin-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 2rem;
}

.admin-brand {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.admin-brand h1 {
  margin: 0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}

.back-to-site {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.back-to-site:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  transform: translateY(-1px);
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.dashboard-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 4.5rem;
}

.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-card h3 {
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-number {
  margin: 0;
  font-size: 2rem;
  font-weight: bold;
  color: #333;
}

.posts-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.section-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  color: #333;
}

.filters select {
  padding: 0.5rem;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  background: white;
}

.loading, .error, .empty-state {
  padding: 3rem;
  text-align: center;
  color: #666;
}

.empty-state i {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 1rem;
}

.posts-table {
  overflow-x: auto;
}

.posts-table table {
  width: 100%;
  border-collapse: collapse;
}

.posts-table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e1e5e9;
}

.posts-table td {
  padding: 1rem;
  border-bottom: 1px solid #e1e5e9;
}

.post-title strong {
  display: block;
  color: #333;
}

.post-slug {
  font-size: 0.875rem;
  color: #666;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.published {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  padding: 0.5rem;
  border: none;
  background: #f8f9fa;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: #e9ecef;
  color: #333;
}

.btn-icon.btn-danger:hover {
  background: #dc3545;
  color: white;
}

.pagination {
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  border-top: 1px solid #e1e5e9;
}

.page-info {
  color: #666;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .admin-header {
    height: auto;
    min-height: 3.8rem;
  }

  .admin-nav {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
  }

  .admin-brand {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }

  .admin-brand h1 {
    font-size: 1.25rem;
  }

  .header-actions {
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn-text {
    display: none;
  }

  .dashboard-content {
    padding: 1rem;
    margin-top: 4rem;
  }

  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .posts-table {
    font-size: 0.875rem;
  }

  .action-buttons {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .admin-header {
    min-height: 3.5rem;
  }

  .dashboard-content {
    margin-top: 3.5rem;
  }

  .btn {
    padding: 0.75rem;
    min-width: 44px;
    justify-content: center;
  }

  .header-actions {
    gap: 0.5rem;
  }
}
</style>